import { Empty, Spinner, VirtualList, lucideIcons } from '@round/creatorbase-ui';
import { ProjectCampaignOption, ProjectCampaignOptionGroup } from '../useProjectCampaignGroups';

import CampaignItem from '../CampaignItem/CampaignItem';
import ProjectItem from '../ProjectItem/ProjectItem';
import styles from './ProjectCampaignsList.module.css';

const { FolderSearch, AlertTriangle } = lucideIcons;

const SCROLL_END_THRESHOLD_PX = 200;

export type ProjectCampaignsListProps = {
    groups: ProjectCampaignOptionGroup[];
    isLoading: boolean;
    hasError: boolean;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    onEndReached: () => void;
    onSelect: (campaign: ProjectCampaignOption) => void;
    selectedCampaign: ProjectCampaignOption | null;
};

const ProjectCampaignsList = ({
    groups,
    isLoading,
    hasError,
    isFetchingNextPage,
    onEndReached,
    onSelect,
    selectedCampaign,
}: ProjectCampaignsListProps) => {
    return (
        <div className={styles.listColumn}>
            {isLoading && groups.length === 0 ? (
                <div className={styles.listState}>
                    <Spinner />
                </div>
            ) : null}

            {!isLoading && !hasError && groups.length === 0 ? (
                <Empty>
                    <Empty.Header>
                        <Empty.Media variant="icon">
                            <FolderSearch />
                        </Empty.Media>
                        <Empty.Title>No campaigns found</Empty.Title>
                        <Empty.Description>Try another search or check back later.</Empty.Description>
                    </Empty.Header>
                </Empty>
            ) : null}

            {hasError && !isLoading ? (
                <Empty>
                    <Empty.Header>
                        <Empty.Media variant="icon">
                            <AlertTriangle />
                        </Empty.Media>
                    </Empty.Header>
                    <Empty.Description>Something went wrong fetching campaigns</Empty.Description>
                </Empty>
            ) : null}

            {groups.length > 0 ? (
                <VirtualList
                    className={styles.listViewport}
                    count={groups.length}
                    estimateSize={52}
                    overscan={6}
                    onEndReached={onEndReached}
                    endReachedThreshold={SCROLL_END_THRESHOLD_PX}
                >
                    {(virtualRow) => {
                        const group = groups[virtualRow.index];

                        return (
                            <ProjectItem group={group}>
                                {!group.options.length && (
                                    <Empty>
                                        <Empty.Description>No matching campaigns found</Empty.Description>
                                    </Empty>
                                )}

                                {group.options.map((campaign) => {
                                    const isSelected = selectedCampaign?.id === campaign.id;

                                    return (
                                        <CampaignItem
                                            className={styles.campaignItem}
                                            key={campaign.id}
                                            campaign={campaign}
                                            isSelected={isSelected}
                                            onSelect={() => onSelect(campaign)}
                                        />
                                    );
                                })}
                            </ProjectItem>
                        );
                    }}
                </VirtualList>
            ) : null}

            {isFetchingNextPage ? (
                <div className={styles.listFooter}>
                    <Spinner />
                </div>
            ) : null}
        </div>
    );
};

export default ProjectCampaignsList;
